/**
 * Форматирует дату в удобочитаемый формат
 * @param dateString - строка даты (ISO формат)
 * @param locale - локаль для форматирования (по умолчанию 'ru-RU')
 * @param options - опции форматирования
 * @returns отформатированная дата или 'N/A' если дата невалидна
 */
export const formatDate = (
  dateString?: string | Date,
  locale: string = 'ru-RU',
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!dateString) return 'N/A';

  try {
    const date = dateString instanceof Date ? dateString : new Date(dateString);

    // Проверяем, что дата валидна
    if (isNaN(date.getTime())) return 'N/A';

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options,
    };

    return date.toLocaleDateString(locale, defaultOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

/**
 * «25 октября 2026 г.» без времени — для сроков: у срока есть день, но нет часа.
 * `YYYY-MM-DD` печатается как есть, без пересчёта в пояс браузера.
 */
export const formatDay = (dateString?: string | Date, locale: string = 'ru-RU'): string =>
  typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)
    ? formatDate(`${dateString}T00:00:00Z`, locale, {
        hour: undefined,
        minute: undefined,
        timeZone: 'UTC',
      })
    : formatDate(dateString, locale, { hour: undefined, minute: undefined });

/**
 * День срока цели `YYYY-MM-DD`. Сервер отдаёт его сам (`deadlineDay`, по поясу компании).
 * Без него — то же правило: ровно полночь UTC — старая запись, её день — дата UTC;
 * иначе дата в поясе браузера.
 */
export const goalDeadlineDay = (goal: {
  deadline?: string;
  deadlineDay?: string;
}): string | undefined => {
  if (goal.deadlineDay) return goal.deadlineDay;
  if (!goal.deadline) return undefined;
  if (goal.deadline.endsWith('T00:00:00.000Z')) return goal.deadline.slice(0, 10);
  const d = new Date(goal.deadline);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * Форматирует дату в короткий формат (только дата)
 * @param dateString - строка даты (ISO формат)
 * @param locale - локаль для форматирования (по умолчанию 'ru-RU')
 * @returns отформатированная дата или 'N/A' если дата невалидна
 */
export const formatDateShort = (dateString?: string | Date, locale: string = 'ru-RU'): string => {
  // hour/minute сидят в defaultOptions функции formatDate, поэтому их надо
  // гасить явно — иначе «короткий» формат печатал ещё и время.
  return formatDate(dateString, locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: undefined,
    minute: undefined,
  });
};

/**
 * Форматирует дату в формат с временем
 * @param dateString - строка даты (ISO формат)
 * @param locale - локаль для форматирования (по умолчанию 'ru-RU')
 * @returns отформатированная дата с временем или 'N/A' если дата невалидна
 */
export const formatDateTime = (dateString?: string | Date, locale: string = 'ru-RU'): string => {
  return formatDate(dateString, locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Считает количество полных месяцев в команде от даты приёма до текущего момента
 * @param hireDate - дата приёма (ISO строка или Date)
 * @returns число полных месяцев, либо null если дата не задана/невалидна
 */
export const getMonthsInTeam = (hireDate?: string | Date): number | null => {
  if (!hireDate) return null;

  const start = hireDate instanceof Date ? hireDate : new Date(hireDate);
  if (isNaN(start.getTime())) return null;

  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());

  // Текущий день месяца ещё не достиг дня приёма — месяц неполный
  if (now.getDate() < start.getDate()) months -= 1;

  return months < 0 ? 0 : months;
};

/**
 * Форматирует время последнего онлайна в относительный формат
 * @param dateString - строка даты (ISO формат)
 * @param locale - локаль для форматирования (по умолчанию 'ru-RU')
 * @returns относительное время или 'N/A' если дата невалидна
 */
export const formatLastOnline = (dateString?: string | Date, locale: string = 'ru-RU'): string => {
  if (!dateString) return 'N/A';

  try {
    const date = dateString instanceof Date ? dateString : new Date(dateString);

    // Проверяем, что дата валидна
    if (isNaN(date.getTime())) return 'N/A';

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (locale === 'ru-RU' || locale === 'ru') {
      if (diffInMinutes < 1) return 'только что';
      if (diffInMinutes < 60) return `${diffInMinutes} мин назад`;
      if (diffInHours < 24) return `${diffInHours} ч назад`;
      if (diffInDays < 7) return `${diffInDays} д назад`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} нед назад`;

      // Для более старых дат показываем полный формат
      return formatDate(dateString, locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } else {
      // English
      if (diffInMinutes < 1) return 'just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInHours < 24) return `${diffInHours}h ago`;
      if (diffInDays < 7) return `${diffInDays}d ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`;

      // Для более старых дат показываем полный формат
      return formatDate(dateString, locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
  } catch (error) {
    console.error('Error formatting last online:', error);
    return 'N/A';
  }
};
